/**
 * The palette and typography are taken verbatim from BEETS' theme.ts, so the
 * two tools read as one family. Where BEST needs something BEETS has no use
 * for it is added below rather than by changing a shared value.
 *
 * BEETS runs MUI 5; this is MUI 9. The tokens transfer unchanged - they are
 * Material Design values, not API surface - but component overrides are not
 * copy-pasteable between the two.
 */

import type { Theme } from '@mui/material/styles'
import { createTheme } from '@mui/material/styles'
import type { ThemeOptions } from '@mui/material/styles'

const sharedTypography: ThemeOptions['typography'] = {
  fontFamily: [
    '-apple-system',
    'BlinkMacSystemFont',
    '"Segoe UI"',
    'Roboto',
    '"Helvetica Neue"',
    'Arial',
    'sans-serif',
  ].join(','),
  h1: { fontSize: '2.5rem', fontWeight: 500 },
  h2: { fontSize: '2rem', fontWeight: 500 },
  h3: { fontSize: '1.75rem', fontWeight: 500 },
}

/**
 * Anything that IS an identifier in the model - a requirement ID, a qualified
 * name, a SysML type - is set in monospace, because those are strings people
 * compare character by character.
 */
export const MONOSPACE = [
  'ui-monospace',
  '"Cascadia Mono"',
  '"SF Mono"',
  'Menlo',
  'Consolas',
  'monospace',
].join(',')

const sharedComponents: ThemeOptions['components'] = {
  MuiButton: {
    styleOverrides: {
      root: { textTransform: 'none' as const },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: { boxShadow: '0 1px 3px rgba(0,0,0,0.12)' },
    },
  },
  // Dense by default: these screens are tables of identifiers, and the
  // comfortable default wastes a third of the rows on a laptop.
  MuiTable: {
    defaultProps: { size: 'small' as const },
  },

  /**
   * EVERY LINK TAKES THE PALETTE, including the ones that are not MUI's.
   *
   * React Router's `Link` renders a plain `<a>`, so `MuiLink`'s styles never
   * reach it and it falls back to the USER AGENT's `#0000EE` - and its visited
   * purple, which is worse. On the dark theme's `#1e1e1e` paper that is a link
   * somebody has to hunt for; the contrast is around 2:1, well under the 4.5:1
   * text minimum.
   *
   * Eleven of them across the app, so this is one rule rather than eleven
   * edits - and a twelfth written tomorrow is covered without anybody
   * remembering to wrap it. `primary.main` is `#90caf9` on dark and `#1976d2`
   * on light, both of which are the accent the rest of the app already uses.
   */
  MuiCssBaseline: {
    styleOverrides: (theme: Theme) => ({
      a: {
        color: theme.palette.primary.main,
        // Underlined only on hover, which is what MuiLink does by default -
        // so a router link and a MUI link do not read as two different things.
        textDecorationColor: 'currentColor',
      },
    }),
  },
}

export const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: '#1976d2', light: '#42a5f5', dark: '#1565c0' },
    secondary: { main: '#dc004e', light: '#e33371', dark: '#9a0036' },
    background: { default: '#f5f5f5', paper: '#ffffff' },
  },
  typography: sharedTypography,
  components: sharedComponents,
})

export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#90caf9', light: '#e3f2fd', dark: '#42a5f5' },
    secondary: { main: '#f48fb1', light: '#fce4ec', dark: '#f06292' },
    background: { default: '#121212', paper: '#1e1e1e' },
  },
  typography: sharedTypography,
  components: sharedComponents,
})

export type ThemeMode = 'light' | 'dark'

export function getTheme(mode: ThemeMode) {
  return mode === 'dark' ? darkTheme : lightTheme
}

export default lightTheme
